import { useCallback, useEffect, useLayoutEffect, useRef, useState, type RefObject } from 'react';

/**
 * useContinuousDays — 주간·일간 뷰 "끊기지 않는 가로 연속 스크롤"의 창(window) 관리.
 *
 * useContinuousWeeks.ts(월간 뷰, 세로 스크롤)와 완전히 같은 손수-windowing 패턴을 가로축에
 * 그대로 옮긴 것 — react-window 등 신규 의존성 없이, 좌/우 가장자리 근처(EDGE_PREFETCH_PX)에
 * 닿으면 그쪽으로 EXTEND_UNITS*perView 일만큼 더 붙이고, 총 개수가 상한을 넘으면 반대쪽
 * (화면 밖) 끝에서 잘라낸다. `AXI(캘린더) 목업.html`의 buildDayStream/extendStream 이 이
 * 패턴의 원안(perView=7 이면 주간, perView=1 이면 일간 — 같은 날짜 스트림을 밀도만 다르게
 * 그린다)이다.
 *
 * 월간 뷰(세로)와의 차이 — 주 행 높이(WEEK_ROW_HEIGHT_PX)는 CSS 로 고정한 상수였지만, 날짜
 * 열 폭(colWidth)은 컨테이너 폭에 따라 달라진다(뷰포트·사이드바 접힘 등) — 그래서 이 훅은
 * 마운트 시 + ResizeObserver 로 컨테이너 폭을 직접 측정해 colWidth 를 다시 계산하고, 폭이
 * 바뀌면 "지금 보이던 첫 날짜"가 그대로 보이도록 scrollLeft 를 다시 앵커링한다.
 *
 * ⚠ 가로/세로 스크롤이 같은 컨테이너에서 함께 일어난다(시간 축은 세로, 날짜 축은 가로) —
 * 이 훅은 가로(scrollLeft)만 관리한다. 세로(시간대) 스크롤은 브라우저 기본 동작 그대로.
 */

export const DAY_COL_MIN_WIDTH_PX = 96; // 목업의 Math.max(96, ...) 과 동일한 최소 폭.
export const GUTTER_WIDTH_PX = 56; // .calendar-timegrid-gutter 폭과 반드시 일치시킨다.
const INITIAL_PAST_UNITS = 6;
const INITIAL_FUTURE_UNITS = 6; // 초기 (6+6+1)*perView 일 렌더 — perView=7 이면 91일, 월간 뷰(91일)와 동형.
const EXTEND_UNITS = 6;
const MAX_UNITS = 24; // 상한도 월간 뷰(MAX_WEEKS=24)와 동형 — 넘으면 반대쪽 끝을 잘라 DOM 노드 수를 억제.
const EDGE_PREFETCH_UNITS = 2; // 가장자리에서 이 정도(단위) 남았을 때 미리 확장.

/** 로컬 자정. */
export function startOfDay(d: Date): Date {
  const s = new Date(d);
  s.setHours(0, 0, 0, 0);
  return s;
}
/** 로컬 자정, 일요일 기준 그 주의 시작일. */
export function weekStartOfDay(d: Date): Date {
  const s = startOfDay(d);
  s.setDate(s.getDate() - s.getDay());
  return s;
}
function addDays(d: Date, n: number): Date {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
}
function sameDay(a: Date, b: Date): boolean {
  return a.getTime() === b.getTime();
}
function buildWindow(center: Date, perView: number): Date[] {
  const first = perView === 7 ? weekStartOfDay(center) : startOfDay(center);
  const start = addDays(first, -INITIAL_PAST_UNITS * perView);
  const count = (INITIAL_PAST_UNITS + INITIAL_FUTURE_UNITS + 1) * perView;
  const out: Date[] = [];
  for (let i = 0; i < count; i++) out.push(addDays(start, i));
  return out;
}
function measureColWidth(container: HTMLElement, perView: number): number {
  const usable = Math.max(0, (container.clientWidth || 0) - GUTTER_WIDTH_PX);
  const w = Math.floor(usable / perView);
  return w > DAY_COL_MIN_WIDTH_PX ? w : DAY_COL_MIN_WIDTH_PX;
}

interface UseContinuousDaysArgs {
  containerRef: RefObject<HTMLDivElement>;
  initialDate: Date;
  /** 7=주간(밀도), 1=일간(밀도) — 마운트된 날짜 스트림 자체는 공유되는 개념, 밀도만 다르다. */
  perView: 1 | 7;
  /** 마운트된 날짜 범위가 바뀔 때마다(확장·점프) 그 전체 구간을 알려준다. */
  onNeedRange: (from: Date, to: Date) => void;
  /** 화면 좌측에 걸린 날짜가 바뀔 때 — 헤더 타이틀 동기화용. */
  onVisibleDayChange?: (date: Date) => void;
}

export function useContinuousDays({ containerRef, initialDate, perView, onNeedRange, onVisibleDayChange }: UseContinuousDaysArgs) {
  const [days, setDays] = useState<Date[]>(() => buildWindow(initialDate, perView));
  const [colWidth, setColWidth] = useState(DAY_COL_MIN_WIDTH_PX);

  const extendingRef = useRef(false);
  const pendingScrollDeltaRef = useRef(0);
  /**
   * ⭐⭐ «픽셀»이 아니라 «몇 번째 칸»을 적어 둔다 (2026-09-29 운영 결함 고침).
   *
   * 종전에는 px 를 적어 두었는데, 그 값은 «새로 잰 colWidth» 로 계산해 놓고 대입은 «아직 옛 폭으로
   * 렌더된 DOM» 에 하게 되는 창이 있었다(상태 갱신이 한 렌더 늦다). 그러면 같은 px 가 다른 칸을
   * 가리켜 **처음 열 때 제목과 첫 열이 어긋난다**(운영 실측: 창 1440 에서 한 주 앞, 1280 에서 이틀).
   * ⇒ 칸 번호로 적어 두고, **그 폭으로 실제 렌더된 커밋**에서 `번호 × colWidth` 로 대입한다.
   */
  const pendingScrollIndexRef = useRef<number | null>(null);
  const mountedRef = useRef(false);
  const lastVisibleDayRef = useRef('');
  const perViewRef = useRef(perView);
  const colWidthRef = useRef(colWidth);
  colWidthRef.current = colWidth;
  const daysRef = useRef(days);
  daysRef.current = days;
  // 2026-08-25 — useContinuousWeeks.ts(월간 뷰)에서 실사용 중 발견해 고쳤던 두 안전장치를
  // 이 훅(주간·일간, 가로축)에도 이식한다.
  // (1) 메아리 방지(lastSelfScrollLeftRef) — 보정 effect 가 쓴 scrollLeft 가 브라우저에서
  //     다시 'scroll' 이벤트를 내(메아리), extendPast/Future 를 잘못 재판정하는 걸 막는다.
  //     이건 이 훅에서도 그대로 실사용 가능한 시나리오라 테스트로 재현·검증했다.
  // (2) 방향 추적(prevScrollLeftRef) — weeks 는 WEEK_ROW_HEIGHT_PX 가 뷰포트 크기와
  //     무관한 고정값이라, 뷰포트를 행 높이보다 훨씬 크게 잡으면(예: 2500px) 상한(24행)
  //     도달 시 전체 스크롤 가능 범위가 보정 폭(720px)보다 작아져 한 번의 보정이 반대쪽
  //     가장자리까지 넘어가 버렸다. 이 훅은 colWidth 자체가 클라이언트 폭에 비례해 정해져서
  //     ("뷰포트 안에 perView 개 열이 들어차도록" 항상 재계산) 그 조건(상한 도달 시 스크롤
  //     범위가 뷰포트보다 작아지는 상황)을 뷰포트 크기 선택만으로는 재현할 수 없었다(직접
  //     확인 — 여러 뷰포트 폭으로 시도해도 상한 시 스크롤 범위가 항상 뷰포트의 20배 이상
  //     남는다). 그래도 같은 windowing 패턴이 미래에 바뀌거나(예: colWidth 상한 도입) 다른
  //     경로로 같은 조건이 생길 수 있어, 방어적으로 weeks 와 대칭을 맞춰 둔다 — 위험은
  //     없고(정상 스크롤 방향에서는 항상 무해) weeks 와 코드 모양이 갈라지는 것만 막는다.
  const lastSelfScrollLeftRef = useRef<number | null>(null);
  const prevScrollLeftRef = useRef<number | null>(null);

  // 최초 마운트 — 컨테이너 폭을 측정해 colWidth 를 정하고, 초기 scrollLeft(과거 6*perView일
  // 지난 지점)를 잡고, 초기 창의 이벤트 구간을 요청 + 헤더 동기화.
  //
  // ⚠ scrollLeft 를 여기서 직접 못 건드린다(실제로 겪은 버그) — setColWidth(cw) 는 이 effect
  // 안에서 동기적으로 DOM 에 반영되지 않는다(React 가 리렌더를 큐잉만 함). 그 순간 DOM 은 아직
  // 이전 colWidth(초기값 96px)로 렌더돼 있어 scrollWidth 가 좁고, 거기에 큰 scrollLeft 를
  // 대입하면 브라우저가 즉시 클램프해버린다(실측: 3708 요청 → 630 으로 잘림) — 그 뒤 리렌더로
  // DOM 이 넓어져도 scrollLeft 는 그 잘린 값 그대로 남아 엉뚱한 위치(화면 왼쪽 가장자리 근처)가
  // 되고, 그게 다시 onScroll 의 "가장자리 근접"으로 오판정돼 불필요한 extendPast 까지 유발했다.
  // → 아래 [days, colWidth] effect(항상 DOM 이 최신 colWidth 로 반영된 뒤에 실행됨)가 실제
  // scrollLeft 대입을 맡도록 pendingScrollLeftRef 에만 적어 둔다.
  useLayoutEffect(() => {
    if (mountedRef.current) return;
    mountedRef.current = true;
    const el = containerRef.current;
    const cw = el ? measureColWidth(el, perView) : DAY_COL_MIN_WIDTH_PX;
    setColWidth(cw);
    pendingScrollIndexRef.current = INITIAL_PAST_UNITS * perView;
    onNeedRange(days[0]!, addDays(days[days.length - 1]!, 1));
    const d = days[INITIAL_PAST_UNITS * perView]!;
    onVisibleDayChange?.(d);
    lastVisibleDayRef.current = String(+d);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // perView 가 바뀌면(주간⇄일간 토글) 지금 보이던 첫 날짜를 중심으로 창을 다시 세운다 —
  // colWidth 도 새 perView 기준으로 재측정.
  useLayoutEffect(() => {
    if (!mountedRef.current || perViewRef.current === perView) return;
    perViewRef.current = perView;
    const el = containerRef.current;
    const idx = el ? Math.max(0, Math.min(days.length - 1, Math.round(el.scrollLeft / colWidthRef.current))) : 0;
    const anchor = days[idx] ?? initialDate;
    const next = buildWindow(anchor, perView);
    const cw = el ? measureColWidth(el, perView) : DAY_COL_MIN_WIDTH_PX;
    setColWidth(cw);
    pendingScrollIndexRef.current = INITIAL_PAST_UNITS * perView;
    setDays(next);
    onNeedRange(next[0]!, addDays(next[next.length - 1]!, 1));
    const d = next[INITIAL_PAST_UNITS * perView]!;
    lastVisibleDayRef.current = String(+d);
    onVisibleDayChange?.(d);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [perView]);

  // days 또는 colWidth 가 바뀔 때마다(확장·트리밍·점프·폭 재측정) 시각적 점프를 보정.
  //
  // ⚠ colWidth 를 이 effect 의 의존성에 넣어도, mount effect 가 같은 커밋 안에서
  // setColWidth 를 부른 직후에는 이 effect 가 "아직 안 바뀐"(이전 렌더의) colWidth 로 먼저
  // 한 번 실행된다(React 가 같은 커밋의 레이아웃 이펙트들을 선언 순서대로 실행하고, 상태
  // 갱신은 다음 렌더에야 반영되기 때문) — 그때 DOM 은 여전히 좁은 상태라 scrollWidth 가
  // 작고, pendingScrollLeftRef 를 그대로 대입하면 브라우저가 클램프해버린다(실제로 겪음).
  // 그래서 "DOM 이 그 값을 받아들일 수 있을 만큼 충분히 넓어졌을 때만" 소비하고, 아직
  // 좁으면 그대로 남겨 둬 다음(진짜 colWidth 가 반영된) 실행에서 마저 적용되게 한다.
  // (jsdom 등 실제 레이아웃을 안 하는 환경은 clientWidth 가 항상 0 이라 이 가드가 영원히
  // 막아버릴 수 있다 — clientWidth===0 이면 "잴 수 있는 레이아웃 자체가 없다"는 뜻이므로
  // 그 환경에서는 원래처럼 즉시 적용한다.)
  useLayoutEffect(() => {
    const el = containerRef.current;
    if (el) {
      const pendingIdx = pendingScrollIndexRef.current;
      if (pendingIdx != null) {
        // ⭐⭐ 「지금 렌더된 폭 = 지금 재는 폭」일 때만 소비한다 (2026-09-29).
        //   마운트 커밋에서는 `setColWidth(잰 값)` 이 아직 반영되지 않아 DOM 이 «옛 폭»(96)으로
        //   그려져 있다. 그때 곱해 버리면 «다른 칸»에 가서 선다 — 그것이 운영 결함이었다.
        //   ⇒ 잰 값과 렌더된 값이 같아지는 «다음» 실행에서 대입한다.
        //   ⚠ 레이아웃이 없는 환경(jsdom 기본)은 clientWidth 가 0 이라 잴 수 없다 ⇒ 즉시 적용
        //     (그 환경에서는 colWidth 가 하한 96 으로 «결정적»이라 어긋날 것이 없다).
        const measured = el.clientWidth === 0 ? colWidth : measureColWidth(el, perViewRef.current);
        if (measured === colWidth) {
          el.scrollLeft = pendingIdx * colWidth;
          pendingScrollIndexRef.current = null;
          prevScrollLeftRef.current = null; // 불연속 점프(scrollToDate) — 방향 추적 리셋.
          lastSelfScrollLeftRef.current = el.scrollLeft;
        } // else: 아직 옛 폭으로 렌더된 커밋 — 다음 실행을 기다린다.
      } else if (pendingScrollDeltaRef.current !== 0) {
        el.scrollLeft += pendingScrollDeltaRef.current;
        // 방향 기준점도 같은 폭만큼 옮겨야 보정 직후 다음 실제 스크롤의 방향 판정이 안 어긋난다.
        if (prevScrollLeftRef.current != null) prevScrollLeftRef.current += pendingScrollDeltaRef.current;
        pendingScrollDeltaRef.current = 0;
        lastSelfScrollLeftRef.current = el.scrollLeft;
      }
    }
    extendingRef.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days, colWidth]);

  // 컨테이너 폭 변화(창 크기·사이드바 접힘 등) — colWidth 를 다시 재고, 보던 첫 날짜를 그대로
  // 유지하도록 scrollLeft 를 재앵커링한다. jsdom 등 ResizeObserver 미지원 환경에선 조용히 무시.
  // scrollLeft 는 여기서 직접 대입하지 않는다(위 마운트 effect 주석과 같은 이유 — setColWidth
  // 가 아직 DOM 에 반영되기 전이라 클램프될 수 있다) — pendingScrollLeftRef 에 적어 두면 위
  // [days, colWidth] effect 가 리렌더 이후 정확한 시점에 대신 대입한다.
  useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof ResizeObserver !== 'function') return;
    const ro = new ResizeObserver(() => {
      const nextCw = measureColWidth(el, perViewRef.current);
      if (nextCw === colWidthRef.current) return;
      // daysRef(항상 최신) — 이 effect 는 마운트 시 1회만 설정돼([] deps) `days` 를 직접 쓰면
      // 마운트 시점 창 길이로 고정된다. 브라우저 확대/축소(Ctrl+=/-) 로 컨테이너 폭이 바뀔 때,
      // 그 전에 스크롤로 창이 한 번이라도 늘어나 있었다면(extendPast/Future) 이 고정 길이가
      // 실제보다 작아 idx 를 잘못 깎아버리고, 엉뚱한 날짜로 스크롤이 튀는 버그였다(실사용 보고:
      // "화면 너비 때문에 ctrl +/- 를 누르면 달력이 이동한다").
      const idx = Math.max(0, Math.min(daysRef.current.length - 1, Math.round(el.scrollLeft / colWidthRef.current)));
      colWidthRef.current = nextCw;
      pendingScrollIndexRef.current = idx;
      setColWidth(nextCw);
    });
    ro.observe(el);
    return () => ro.disconnect();
    // 마운트 시 1회 설정 — 이 컨테이너 DOM 노드는 컴포넌트 생애 동안 바뀌지 않는다
    // (MonthView 의 스크롤 훅과 동일 전제). containerRef.current 는 ref 라 의존성 배열에
    // 넣어도 재실행을 트리거하지 않으므로 의도적으로 [] 로 둔다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function extendPast() {
    if (extendingRef.current) return;
    extendingRef.current = true;
    const firstDay = days[0]!;
    const n = EXTEND_UNITS * perView;
    const added = Array.from({ length: n }, (_, i) => addDays(firstDay, -(n - i)));
    let next = [...added, ...days];
    const maxDays = MAX_UNITS * perView;
    if (next.length > maxDays) next = next.slice(0, maxDays); // 미래쪽(화면 밖) 끝을 자른다 — 보정 불필요.
    pendingScrollDeltaRef.current += added.length * colWidthRef.current;
    setDays(next);
    onNeedRange(added[0]!, firstDay);
  }

  function extendFuture() {
    if (extendingRef.current) return;
    extendingRef.current = true;
    const lastDay = days[days.length - 1]!;
    const n = EXTEND_UNITS * perView;
    const added = Array.from({ length: n }, (_, i) => addDays(lastDay, i + 1));
    let next = [...days, ...added];
    const maxDays = MAX_UNITS * perView;
    if (next.length > maxDays) {
      const trimCount = next.length - maxDays;
      next = next.slice(trimCount); // 과거쪽(화면 왼쪽) 끝을 자른다 — 그만큼 scrollLeft 도 줄여야 안 튄다.
      pendingScrollDeltaRef.current -= trimCount * colWidthRef.current;
    }
    setDays(next);
    onNeedRange(addDays(lastDay, 1), addDays(added[added.length - 1]!, 1));
  }

  const rafPendingRef = useRef(false);
  function onScroll() {
    if (rafPendingRef.current) return;
    rafPendingRef.current = true;
    requestAnimationFrame(() => {
      rafPendingRef.current = false;
      const el = containerRef.current;
      if (!el) return;
      const { scrollLeft, scrollWidth, clientWidth } = el;

      // 헤더용 "화면 왼쪽에 걸린 날짜" — 2026-09-09 리포트("가로로 무한 스크롤하면 헤더 날짜가 안 맞는다") 로 두 가지를 고쳤다:
      //   1) 이 rAF 콜백은 onScroll 이 만들어진 렌더의 days 를 물고 있어, 확장·트리밍으로 배열이 바뀐 뒤엔
      //      같은 인덱스가 다른 날짜를 가리켰다 → 항상 최신 daysRef 로 계산한다.
      //   2) 아래 echo 판정(보정 스크롤 건너뛰기)보다 먼저 계산한다 — 트리밍 보정 직후에도 헤더는 맞아야 한다.
      {
        const cur = daysRef.current;
        const idx = Math.max(0, Math.min(cur.length - 1, Math.round(scrollLeft / colWidthRef.current)));
        const d = cur[idx];
        if (d) {
          const key = String(+d);
          if (key !== lastVisibleDayRef.current) {
            lastVisibleDayRef.current = key;
            onVisibleDayChange?.(d);
          }
        }
      }

      // 우리가 방금 보정 effect 에서 쓴 값과 (거의) 같으면 그 메아리 — extendPast/Future
      // 재판정 없이 건너뛴다(useContinuousWeeks.ts 의 lastSelfScrollTopRef 와 동형). 1px
      // 허용 오차는 브라우저 배율(디스플레이 스케일링·페이지 줌)의 서브픽셀 반올림 때문.
      if (lastSelfScrollLeftRef.current != null) {
        const isEcho = Math.abs(scrollLeft - lastSelfScrollLeftRef.current) < 1;
        lastSelfScrollLeftRef.current = null;
        if (isEcho) return;
      }

      // 위치만 보지 않고 방향도 본다 — 실제로 오른쪽으로 스크롤 중이면 extendPast 를, 왼쪽으로
      // 스크롤 중이면 extendFuture 를 부르지 않는다(useContinuousWeeks.ts 의 prevScrollTopRef
      // 와 동형 — 방어적 이식, 위 daysRef 주석 참조: 이 훅은 colWidth 가 뷰포트 폭에 비례해
      // weeks 가 겪은 정확한 재현 조건은 못 만들었지만, 코드 모양·보호 범위는 맞춰 둔다).
      // 처음 평가면 위치만으로 판단.
      const prevScrollLeft = prevScrollLeftRef.current;
      prevScrollLeftRef.current = scrollLeft;
      const scrollingRight = prevScrollLeft != null && scrollLeft > prevScrollLeft;
      const scrollingLeft = prevScrollLeft != null && scrollLeft < prevScrollLeft;

      const edgePx = EDGE_PREFETCH_UNITS * perView * colWidthRef.current;
      if (scrollLeft < edgePx && !scrollingRight) extendPast();
      if (scrollWidth - scrollLeft - clientWidth < edgePx && !scrollingLeft) extendFuture();
    });
  }

  /** 임의의 날짜로 점프 — 이미 마운트된 날짜면 부드럽게 스크롤, 아니면 그 날짜를 중심으로 창을 다시 세운다. */
  const scrollToDate = useCallback((date: Date) => {
    /**
     * ⭐ 주간(perView=7)이면 «그 날이 든 주의 첫 날»에 세운다 (2026-09-29 운영 결함 고침).
     *
     * 종전에는 그 «날짜»를 왼쪽 끝에 두었다. 그러면 오늘이 화요일일 때 화면은 화~월인데
     * 머리 제목은 «그 주»(일~토)를 적어 **제목과 첫 열이 어긋난다**(운영 실측: 제목 9.27~10.3 ·
     * 첫 열 29). 창을 새로 세우는 갈래(`buildWindow`)는 이미 주 시작으로 맞추고 있었으므로,
     * 「이미 마운트된 날짜로 뛰는」 갈래만 어긋나 있었다 — 둘을 같은 규칙으로 맞춘다.
     * ⚠ 일간(perView=1)은 그대로 그 날짜다.
     */
    const target = perViewRef.current === 7 ? weekStartOfDay(date) : startOfDay(date);
    const idx = days.findIndex((d) => sameDay(d, target));
    if (idx !== -1) {
      const el = containerRef.current;
      if (!el) return;
      // useContinuousWeeks.ts 의 scrollToDate 와 동형 — scrollTo({behavior:'smooth'}) 로
      // 애니메이션 스크롤하면, 그 애니메이션이 EDGE_PREFETCH_UNITS 문턱을 지나가는 중에
      // onScroll 이 extendPast/extendFuture 를 발동시켜 그 보정(el.scrollLeft 직접 대입)이
      // 진행 중이던 브라우저 smooth 스크롤을 즉시 중단시킨다 — 목표(그 날짜)가 아니라 보정이
      // 멈춘 아무 위치에 landing 한다. 즉시 대입 + echo 표시 + 방향 추적 리셋으로 애니메이션
      // 자체를 없앤다.
      el.scrollLeft = idx * colWidthRef.current;
      // ⭐ 폭이 «곧 바뀔» 참이면(마운트 직후처럼 잰 값이 아직 렌더 안 됨) 칸 번호를 남겨,
      //   그 폭으로 렌더된 커밋에서 다시 맞추게 한다. 안 그러면 이 px 가 다른 칸을 가리킨다.
      const measuredNow = measureColWidth(el, perViewRef.current);
      if (measuredNow !== colWidthRef.current) pendingScrollIndexRef.current = idx;
      lastSelfScrollLeftRef.current = el.scrollLeft;
      prevScrollLeftRef.current = null;
      // 2026-09-09 — 이 점프는 위 echo 판정에 걸려 onScroll 이 헤더 갱신을 건너뛰었다(이전/다음·오늘 버튼 뒤
      // 헤더가 지난 위치에 머묾). 여기서 직접 알린다.
      const jumped = days[idx]!;
      lastVisibleDayRef.current = String(+jumped);
      onVisibleDayChange?.(jumped);
      return;
    }
    const next = buildWindow(target, perViewRef.current);
    pendingScrollIndexRef.current = INITIAL_PAST_UNITS * perViewRef.current;
    setDays(next);
    onNeedRange(next[0]!, addDays(next[next.length - 1]!, 1));
    const d = next[INITIAL_PAST_UNITS * perViewRef.current]!;
    lastVisibleDayRef.current = String(+d);
    onVisibleDayChange?.(d);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days]);

  return { days, colWidth, onScroll, scrollToDate };
}
